import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Plus, ChevronDown, Mail, Phone, MapPin } from "lucide-react";
import { SITE } from "../lib/site";
import Articles from "./admin/Articles";
import Recipients from "./admin/Recipients";
import { Appraisals, Suppressions } from "./admin/Lists";
import Watchers from "./admin/Watchers";
import Broadcast from "./admin/Broadcast";
import Callbacks from "./admin/Callbacks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "usl_admin_token";
const STATUSES = ["New", "Contacted", "Appraising", "Agreed", "Declined", "Removed"];

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch {
      setErr("Invalid password. Please try again.");
      toast.error("Invalid password.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="admin-login" className="bg-[#0B0F0E] min-h-screen flex items-center">
      <Toaster position="top-center" richColors theme="dark" />
      <div className="max-w-md mx-auto w-full px-6">
        <div className="flex items-center gap-3 mb-10">
          <img src={SITE.logo} alt="" className="h-14 w-14 object-contain" style={{ filter: "brightness(0) saturate(100%) invert(72%) sepia(36%) saturate(490%) hue-rotate(8deg) brightness(92%) contrast(86%)" }} />
          <div>
            <div className="font-display text-2xl text-[#F4EFE6]">UPSURGE</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A24B] font-semibold">Pipeline</div>
          </div>
        </div>
        <h1 className="font-display text-[#F4EFE6] text-5xl leading-[0.95]">Admin <span className="italic-gold">access.</span></h1>
        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <label className="overline block mb-2">Password</label>
            <input data-testid="admin-password" type="password" className="underline-input" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            {err && <p data-testid="admin-login-error" className="text-rose-400 text-xs mt-3">{err}</p>}
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="usl-btn w-full justify-center">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-[#F4EFE6]/10 bg-[#11171A] p-6">
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#9BA39B] font-semibold">{label}</div>
      <div className="font-display text-4xl text-[#C9A24B] mt-3">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tones = {
    New: "border-[#C9A24B]/50 text-[#C9A24B]",
    Contacted: "border-blue-400/40 text-blue-300",
    Appraising: "border-purple-400/40 text-purple-300",
    Agreed: "border-emerald-400/40 text-emerald-300",
    Declined: "border-rose-400/40 text-rose-300",
    Removed: "border-[#9BA39B]/40 text-[#9BA39B]",
  };
  return <span className={`inline-flex border px-2 py-0.5 text-[10px] tracking-[0.22em] uppercase font-semibold ${tones[status] || tones.New}`}>{status}</span>;
}

function SubmissionRow({ s, onChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(s.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/land-submissions/${s.id}`, { status }, { headers: authHeader() });
      onChange(data);
      toast.success(`Marked ${status}`);
    } catch { toast.error("Failed to update."); }
    finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/land-submissions/${s.id}`, { admin_notes: notes }, { headers: authHeader() });
      onChange(data);
      toast.success("Notes saved");
    } catch { toast.error("Failed to save."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete submission from ${s.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/land-submissions/${s.id}`, { headers: authHeader() });
      onDelete(s.id);
      toast.success("Deleted");
    } catch { toast.error("Failed to delete."); }
  };

  return (
    <div data-testid={`row-${s.id}`} className="border-b border-[#F4EFE6]/10">
      <div className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm">
        <div className="col-span-3">
          <div className="font-semibold text-[#F4EFE6]">{s.name}</div>
          <div className="text-[11px] text-[#9BA39B] truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
        </div>
        <div className="col-span-2 text-[#E8E1D2]">{s.location}</div>
        <div className="col-span-1 text-[11px] uppercase tracking-wider text-[#9BA39B]">{s.source || "web"}</div>
        <div className="col-span-2 text-[11px] uppercase tracking-wider text-[#C9A24B]">{s.response_code || s.campaign_code || "—"}</div>
        <div className="col-span-2"><StatusBadge status={s.status} /></div>
        <div className="col-span-2 flex justify-end gap-2">
          <button data-testid={`row-toggle-${s.id}`} onClick={() => setOpen(!open)} className="text-[#9BA39B] hover:text-[#C9A24B] p-1.5"><ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
          <button data-testid={`row-delete-${s.id}`} onClick={remove} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
        </div>
      </div>
      {open && (
        <div className="px-5 pb-6 pt-2 bg-[#0B0F0E]/60 grid md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-2 text-[#E8E1D2]">
            <div><span className="overline">Interest:</span> <span className="ml-2">{s.interest || "—"}</span></div>
            <div><span className="overline">Acreage:</span> <span className="ml-2">{s.acreage || "—"}</span></div>
            <div><span className="overline">Planning:</span> <span className="ml-2">{s.planning_status || "—"}</span></div>
            <div><span className="overline">Notes:</span> <div className="mt-2 text-[#9BA39B] whitespace-pre-wrap">{s.notes || "—"}</div></div>
            <div className="text-[11px] text-[#9BA39B] pt-2">Received {new Date(s.created_at).toLocaleString("en-GB")}</div>
          </div>
          <div className="space-y-4">
            <div>
              <div className="overline mb-2 block">Move to status</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map(st => (
                  <button
                    key={st}
                    data-testid={`row-status-${s.id}-${st}`}
                    onClick={() => updateStatus(st)}
                    disabled={saving || s.status === st}
                    className={`text-[11px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${s.status === st ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]" : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50 hover:text-[#C9A24B]"}`}
                  >{st}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-2 block">Internal notes</div>
              <textarea data-testid={`row-notes-${s.id}`} rows={3} className="underline-input resize-none" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <button data-testid={`row-save-${s.id}`} onClick={saveNotes} disabled={saving} className="usl-btn-outline mt-3 text-xs">Save Notes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pipeline() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/land-submissions`, { headers: authHeader() });
      setItems(data);
    } catch { toast.error("Failed to load."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    (filter === "All" || i.status === filter) &&
    (sourceFilter === "All" || (i.source || "web") === sourceFilter)
  );

  const exportCsv = () => {
    const header = ["name","email","phone","location","acreage","planning_status","source","campaign_code","response_code","interest","status","admin_notes","created_at"];
    const rows = filtered.map(s => header.map(h => `"${String(s[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `upsurge-pipeline-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="overline mr-2">Status:</div>
        {["All", ...STATUSES].map(s => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
            className={`text-[11px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${filter === s ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]" : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50"}`}
          >{s}</button>
        ))}
        <span className="mx-2 h-4 w-px bg-[#F4EFE6]/15"></span>
        <div className="overline mr-2">Source:</div>
        {["All", "web", "letter", "qr", "referral"].map(s => (
          <button key={s} data-testid={`source-${s}`} onClick={() => setSourceFilter(s)}
            className={`text-[11px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${sourceFilter === s ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]" : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50"}`}
          >{s}</button>
        ))}
        <div className="ml-auto flex gap-2">
          <button data-testid="reload-pipeline" onClick={load} className="usl-btn-outline text-xs"><RefreshCw size={14} /> Refresh</button>
          <button data-testid="export-csv" onClick={exportCsv} className="usl-btn text-xs">Export CSV</button>
        </div>
      </div>

      <div className="border border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-[#9BA39B] font-semibold border-b border-[#F4EFE6]/10">
          <div className="col-span-3">Name / Contact</div>
          <div className="col-span-2">Location</div>
          <div className="col-span-1">Source</div>
          <div className="col-span-2">Code</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-[#9BA39B] text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div data-testid="pipeline-empty" className="p-10 text-center text-[#9BA39B] text-sm">No submissions yet.</div>
        ) : filtered.map(s => (
          <SubmissionRow key={s.id} s={s}
            onChange={(u) => setItems(items.map(x => x.id === u.id ? u : x))}
            onDelete={(id) => setItems(items.filter(x => x.id !== id))}
          />
        ))}
      </div>
    </div>
  );
}

function Campaigns() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/campaigns`, { headers: authHeader() });
      setItems(data);
    } catch { toast.error("Failed to load."); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      await axios.post(`${API}/admin/campaigns`, { name, region, notes: "" }, { headers: authHeader() });
      setName(""); setRegion(""); load();
      toast.success("Campaign created");
    } catch { toast.error("Failed"); }
  };

  const remove = async (id, n) => {
    if (!window.confirm(`Delete campaign "${n}"?`)) return;
    try { await axios.delete(`${API}/admin/campaigns/${id}`, { headers: authHeader() }); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <form data-testid="campaign-form" onSubmit={create} className="border border-[#F4EFE6]/10 bg-[#11171A] p-6 grid md:grid-cols-12 gap-4 items-end mb-6">
        <div className="md:col-span-5">
          <label className="overline block mb-2">Campaign name</label>
          <input data-testid="campaign-name" className="underline-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Cotswolds Q1 mailer" />
        </div>
        <div className="md:col-span-4">
          <label className="overline block mb-2">Region</label>
          <input data-testid="campaign-region" className="underline-input" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Gloucestershire" />
        </div>
        <div className="md:col-span-3">
          <button data-testid="campaign-create" type="submit" className="usl-btn w-full justify-center"><Plus size={14} /> Create</button>
        </div>
      </form>

      <div className="border border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-[#9BA39B] font-semibold border-b border-[#F4EFE6]/10">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Region</div>
          <div className="col-span-3">Code (use in letter URL)</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1"></div>
        </div>
        {items.length === 0 ? (
          <div data-testid="campaigns-empty" className="p-10 text-center text-[#9BA39B] text-sm">No campaigns yet.</div>
        ) : items.map(c => (
          <div key={c.id} data-testid={`campaign-row-${c.id}`} className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm border-b border-[#F4EFE6]/10">
            <div className="col-span-3 text-[#F4EFE6]">{c.name}</div>
            <div className="col-span-3 text-[#9BA39B]">{c.region || "—"}</div>
            <div className="col-span-3 font-mono text-[#C9A24B] tracking-wider">{c.code}</div>
            <div className="col-span-2 text-[11px] text-[#9BA39B]">{new Date(c.created_at).toLocaleDateString("en-GB")}</div>
            <div className="col-span-1 text-right">
              <button data-testid={`campaign-delete-${c.id}`} onClick={() => remove(c.id, c.name)} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#9BA39B] mt-4">Tip: send recipients to <span className="text-[#C9A24B]">{window.location.origin}/respond?code=USL-XXXXX&amp;c=CAMPCODE</span></p>
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const { data } = await axios.get(`${API}/admin/contact-messages`, { headers: authHeader() }); setItems(data); }
    catch { toast.error("Failed to load."); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete message?")) return;
    await axios.delete(`${API}/admin/contact-messages/${id}`, { headers: authHeader() });
    load();
  };
  return (
    <div className="space-y-4">
      {items.length === 0 ? <div data-testid="messages-empty" className="p-10 text-center text-[#9BA39B] text-sm border border-[#F4EFE6]/10 bg-[#11171A]">No messages yet.</div> : items.map(m => (
        <div key={m.id} data-testid={`message-${m.id}`} className="border border-[#F4EFE6]/10 bg-[#11171A] p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-[#F4EFE6]">{m.name} {m.subject && <span className="text-[#9BA39B] font-normal">· {m.subject}</span>}</div>
              <div className="text-[11px] text-[#9BA39B] mt-1 flex flex-wrap gap-3">
                <span className="flex items-center gap-1"><Mail size={12} /> {m.email}</span>
                {m.phone && <span className="flex items-center gap-1"><Phone size={12} /> {m.phone}</span>}
                <span>{new Date(m.created_at).toLocaleString("en-GB")}</span>
              </div>
            </div>
            <button data-testid={`message-delete-${m.id}`} onClick={() => remove(m.id)} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
          </div>
          <p className="mt-4 text-sm text-[#E8E1D2] whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("pipeline");
  const [stats, setStats] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) { setChecking(false); return; }
      try {
        await axios.get(`${API}/admin/verify`, { headers: authHeader() });
        setAuthed(true);
      } catch { localStorage.removeItem(TOKEN_KEY); }
      finally { setChecking(false); }
    };
    check();
  }, []);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/admin/stats`, { headers: authHeader() })
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, [authed, tab]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  if (checking) return <div className="bg-[#0B0F0E] min-h-screen flex items-center justify-center text-[#9BA39B]">Loading...</div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div data-testid="admin-page" className="bg-[#0B0F0E] min-h-screen">
      <Toaster position="top-center" richColors theme="dark" />
      <header className="border-b border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={SITE.logo} alt="" className="h-10 w-10 object-contain" style={{ filter: "brightness(0) saturate(100%) invert(72%) sepia(36%) saturate(490%) hue-rotate(8deg) brightness(92%) contrast(86%)" }} />
            <div>
              <div className="font-display text-xl text-[#F4EFE6]">UPSURGE</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A24B] font-semibold">Pipeline · Off-Market CRM</div>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="usl-btn-outline text-xs"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Stat testid="stat-total" label="Total Submissions" value={stats.total_submissions} />
            <Stat testid="stat-new" label="New (Unactioned)" value={stats.by_status.New || 0} />
            <Stat testid="stat-letters" label="From Letters" value={stats.by_source.letter || 0} />
            <Stat testid="stat-campaigns" label="Active Campaigns" value={stats.total_campaigns} />
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6 border-b border-[#F4EFE6]/10">
          {[
            { k: "pipeline", l: "Pipeline" },
            { k: "callbacks", l: "Callbacks" },
            { k: "campaigns", l: "Campaigns" },
            { k: "recipients", l: "Recipients" },
            { k: "watchers", l: "Watch List" },
            { k: "broadcast", l: "Broadcast" },
            { k: "appraisals", l: "Appraisals" },
            { k: "insights", l: "Insights" },
            { k: "messages", l: "Messages" },
            { k: "suppressions", l: "Suppressions" },
          ].map(t => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${tab === t.k ? "text-[#C9A24B] border-b-2 border-[#C9A24B] -mb-px" : "text-[#9BA39B] hover:text-[#F4EFE6]"}`}
            >{t.l}</button>
          ))}
        </div>

        {tab === "pipeline" && <Pipeline />}
        {tab === "callbacks" && <Callbacks />}
        {tab === "campaigns" && <Campaigns />}
        {tab === "recipients" && <Recipients />}
        {tab === "watchers" && <Watchers />}
        {tab === "broadcast" && <Broadcast />}
        {tab === "appraisals" && <Appraisals />}
        {tab === "insights" && <Articles />}
        {tab === "messages" && <Messages />}
        {tab === "suppressions" && <Suppressions />}
      </div>
    </div>
  );
}
